// Copyright ©️ 2026 eVolve MEP, LLC

import './draft-table.css';

import React from 'react';

import type { FacilityId, ShopConfiguration, WorkCell } from '@evolvemep/api-ts';
import type { GridApi } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';

import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';

import { useWorkCellsGrid } from './useWorkCellsGrid';

const NoWorkCellsOverlay = ({ draftExists, api }: { api: GridApi; draftExists: boolean }) =>
  draftExists ? (
    <NoRowsOverlay label="No existing work cells." gridApi={api} />
  ) : (
    <NoRowsOverlay icon="WorkCells" label="Edit setup to get started." gridApi={api} />
  );

type Props = {
  facilityId: FacilityId;
  /** The active, in use `shopConfiguration` */
  activeShopConfiguration: ShopConfiguration;
  draftExists: boolean;
};

export const ActiveWorkCellsGrid = ({ facilityId, activeShopConfiguration, draftExists }: Props) => {
  const { agGridProps, sorting } = useWorkCellsGrid(facilityId, 'active', activeShopConfiguration, draftExists);

  return (
    <AgGridContainer w={draftExists ? '50%' : '100%'} className={draftExists ? 'active-table' : undefined}>
      {(applyWrap) => (
        <AgGridReact<WorkCell>
          loading={sorting}
          {...agGridProps}
          onFirstDataRendered={applyWrap}
          noRowsOverlayComponent={NoWorkCellsOverlay}
          noRowsOverlayComponentParams={{ draftExists }}
        />
      )}
    </AgGridContainer>
  );
};
